import React, { useState, useRef, useEffect, useMemo } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import useStyles from '../../theme/useStyles';
import { parseTime, to12h } from '../../utils/format';

// A 12-hour time picker: hour, minute and AM/PM chosen from three columns in a
// dropdown, with the value always read back as "hh:mm AM/PM".
//
// A native <input type="time"> was tried and dropped: it renders 12- or
// 24-hour purely from the browser/OS locale, so AM/PM simply does not appear on
// a 24-hour locale and no attribute or CSS can force it. The church reads times
// as "08:30 AM", so the control draws its own. Any stored form is accepted
// (24-hour "15:31" included) and "hh:mm AM/PM" is always written back, so only
// a real time can be saved (CHM-012).

export interface TimeFieldProps {
  label?: ReactNode;
  name?: string;
  /** "hh:mm AM/PM" or "HH:MM"; blank when unset. */
  value?: string;
  onChange?: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  error?: ReactNode;
  hint?: ReactNode;
  /** Minute choices; every minute by default. */
  minuteStep?: number;
  fullWidth?: boolean;
  containerStyle?: CSSProperties;
  isDarkMode?: boolean;
}

const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const PERIODS = ['AM', 'PM'];

type Draft = { h: string; m: string; p: string };

const draftFrom = (value?: string): Draft => {
  const t = parseTime(value);
  if (!t) return { h: '', m: '', p: '' };
  return {
    h: String(t.hour24 % 12 || 12).padStart(2, '0'),
    m: String(t.minute).padStart(2, '0'),
    p: t.hour24 >= 12 ? 'PM' : 'AM'
  };
};

const TimeField = ({
  label, name, value, onChange, required = false, disabled = false, error, hint,
  minuteStep = 1, fullWidth = false, containerStyle, isDarkMode
}: TimeFieldProps) => {
  const s = useStyles(isDarkMode);
  const t = s.t;

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(value));
  const wrapperRef = useRef<HTMLDivElement>(null);

  // The field is controlled: whatever the form holds is what the columns show.
  useEffect(() => { setDraft(draftFrom(value)); }, [value]);

  // Close on outside click, the same way SearchableSelect does.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const minutes = useMemo(() => {
    const list = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => String(i * minuteStep).padStart(2, '0'));
    // A stored minute off the step (e.g. 08:35 with a 5-minute step) still shows.
    const m = draftFrom(value).m;
    if (m && !list.includes(m)) list.push(m);
    return list.sort();
  }, [minuteStep, value]);

  // Only a complete hour/minute/AM-PM makes a value; Clear empties all three.
  const commit = (next: Draft) => {
    setDraft(next);
    if (!onChange) return;
    if (!next.h || !next.m || !next.p) { onChange(''); return; }
    const parsed = parseTime(`${next.h}:${next.m} ${next.p}`);
    onChange(parsed ? to12h(parsed) : '');
  };

  /**
   * Picking any one column fills the others with sensible defaults, so a
   * single click already puts a real time in the field. Requiring all three
   * before anything was written meant choosing an hour and forgetting AM/PM
   * left the field empty, and the entry looked as though it had not registered
   * (CHM-106). Every default is visible in the columns and can be changed.
   */
  const pick = (part: Partial<Draft>) => {
    const merged: Draft = { ...draft, ...part };
    commit({ h: merged.h || '12', m: merged.m || '00', p: merged.p || 'AM' });
  };

  const current = parseTime(value);
  const display = current ? to12h(current) : '';

  const columnStyle: CSSProperties = {
    flex: 1, minWidth: 0, maxHeight: '190px', overflowY: 'auto',
    borderRight: `1px solid ${t.border}`, padding: '4px'
  };

  const cellStyle = (active: boolean): CSSProperties => ({
    display: 'block', width: '100%', textAlign: 'center',
    padding: '7px 4px', marginBottom: '2px', cursor: 'pointer',
    border: 'none', borderRadius: t.radius.sm,
    fontSize: t.fontSize.md, fontWeight: active ? t.fontWeight.bold : 'normal',
    backgroundColor: active ? t.primary : 'transparent',
    color: active ? t.textOnPrimary : t.textMuted,
    fontVariantNumeric: 'tabular-nums'
  });

  // Keeps the chosen hour/minute in view when the panel opens on a saved time.
  const scrollIntoView = (el: HTMLButtonElement | null, active: boolean) => {
    if (el && active && open) el.scrollIntoView({ block: 'nearest' });
  };

  const Column = ({ items, selected, onPick, ariaLabel, last = false }: {
    items: string[]; selected: string; onPick: (v: string) => void; ariaLabel: string; last?: boolean;
  }) => (
    <div style={last ? { ...columnStyle, borderRight: 'none' } : columnStyle} role="listbox" aria-label={ariaLabel}>
      {items.map((item) => {
        const active = selected === item;
        return (
          <button
            key={item}
            type="button"
            role="option"
            aria-selected={active}
            ref={(el) => scrollIntoView(el, active)}
            onClick={() => onPick(item)}
            style={cellStyle(active)}
          >
            {item}
          </button>
        );
      })}
    </div>
  );

  return (
    <div style={{ ...s.inputGroup, ...(fullWidth ? s.fullWidth : {}), ...containerStyle }}>
      {label && (
        <label htmlFor={name} style={s.label}>
          {label}
          {required && <span style={{ color: t.error, marginLeft: '4px' }}>*</span>}
        </label>
      )}

      <div ref={wrapperRef} style={{ position: 'relative' }}>
        <button
          type="button"
          id={name}
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
          onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={typeof label === 'string' ? label : 'Time'}
          style={{
            ...s.input,
            ...(error ? { borderColor: t.error } : {}),
            width: '100%', textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px'
          }}
        >
          <span style={{
            color: display ? t.textMain : t.textFaint,
            fontVariantNumeric: 'tabular-nums', letterSpacing: '0.5px'
          }}>
            {display || '--:-- --'}
          </span>
          <span aria-hidden="true" style={{ color: t.textFaint }}>🕐</span>
        </button>

        {open && !disabled && (
          <div
            role="dialog"
            aria-label="Choose a time"
            style={{
              position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px',
              backgroundColor: t.bgRaised, border: `1px solid ${t.border}`,
              borderRadius: t.radius.sm, zIndex: t.zIndex.dropdown,
              boxShadow: '0 6px 18px rgba(0,0,0,0.18)', minWidth: '230px'
            }}
          >
            {/* Hour : Minute : AM/PM, each its own scrollable column. */}
            <div style={{ display: 'flex', alignItems: 'stretch' }}>
              <Column items={HOURS} selected={draft.h} ariaLabel="Hour"
                onPick={(h) => pick({ h })} />
              <Column items={minutes} selected={draft.m} ariaLabel="Minute"
                onPick={(m) => pick({ m })} />
              <Column items={PERIODS} selected={draft.p} ariaLabel="AM or PM" last
                onPick={(p) => pick({ p })} />
            </div>

            <div style={{
              display: 'flex', justifyContent: 'space-between', gap: '8px',
              padding: '8px', borderTop: `1px solid ${t.border}`
            }}>
              <button
                type="button"
                onClick={() => {
                  const now = new Date();
                  commit({
                    h: String(now.getHours() % 12 || 12).padStart(2, '0'),
                    m: String(now.getMinutes()).padStart(2, '0'),
                    p: now.getHours() >= 12 ? 'PM' : 'AM'
                  });
                }}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  color: t.textAccent, fontSize: t.fontSize.sm, fontWeight: 'bold'
                }}
              >
                Now
              </button>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => commit({ h: '', m: '', p: '' })}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    color: t.danger, fontSize: t.fontSize.sm, fontWeight: 'bold'
                  }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    color: t.textMuted, fontSize: t.fontSize.sm, fontWeight: 'bold'
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {hint && !error && <span style={{ fontSize: t.fontSize.sm, color: t.textFaint, marginTop: '4px' }}>{hint}</span>}
      {error && <span style={{ fontSize: t.fontSize.sm, color: t.error, marginTop: '4px' }}>{error}</span>}
    </div>
  );
};

export default TimeField;
